import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { Button, IconButton } from '@volvo/vcdk-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { RouterItem } from '@/types/RouterItem';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import ManualsSelectionModal from '@/components/modals/ManualsSelectionModal';
import PageHeader from '@/components/pageheaders/PageHeader';
import UserMetadataAvatar from '@/components/UserMetadataAvatar';
import { featuresName, isFeatureToggled } from '@/config/EnviromentConfig';
import { getIndividualProductsQueryOptions, unitByIdQueryOptions } from '@/service/vpc/queryOptions';
import { chassis, enviroment, telemetry } from '@/utils';
import { retrieveEndCustomerId } from '@/utils/account';
import { manualsAndHandbooksBaseLink, sparePartsBaseLink } from '@/utils/baseLinks';
import { hasVisibleServices } from '@/utils/services';

const UnitHeader: React.FC = () => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const { endCustomerUnitId } = useParams({
    from: '/_authenticated/_account/units/$endCustomerUnitId',
  });
  const page = useRouterState({
    select: (s) => s.location.state.page,
  });
  const navigate = useNavigate();
  const [showManualsModal, setShowManualsModal] = useState(false);

  const { data: unit, isLoading: isLoadingUnit } = useQuery(unitByIdQueryOptions({ endCustomerUnitId }));
  const serialNumbers = chassis.getUnitSerialNumbers(unit);
  const { data: individualProducts } = useQuery(
    getIndividualProductsQueryOptions({ serialNumbers: serialNumbers || [] })
  );

  const activeAccount = instance.getActiveAccount();
  const endCustomerId = activeAccount ? retrieveEndCustomerId(activeAccount) : undefined;
  // Easy Connect units have their digital-service provisioning hidden, so the Services tab is suppressed.
  const hideEasyConnectServices =
    isFeatureToggled(featuresName.hideEasyConnectProvisioning) &&
    telemetry.isEasyConnectUnit(unit?.telemetryDeviceInfo);

  // IPS40 units skip the ownership check, but every unit needs a service the Services page can
  // render — otherwise the tab only opens onto the empty state (AB#1640979).
  const showDigitalServices =
    unit != null &&
    hasVisibleServices(unit.serviceStatuses) &&
    !hideEasyConnectServices &&
    (chassis.isIps40(unit) || (endCustomerId != null && String(unit.ownedByEndCustomerId) === endCustomerId));

  const items: RouterItem[] = [
    {
      title: t(($) => $.pages.unit.tabs.specifications),
      to: '/units/$endCustomerUnitId/specifications',
      params: { endCustomerUnitId },
    },
    {
      title: t(($) => $.pages.unit.tabs.campaigns),
      to: '/units/$endCustomerUnitId/campaigns',
      params: { endCustomerUnitId },
    },
    {
      title: t(($) => $.pages.unit.tabs.coverage),
      to: '/units/$endCustomerUnitId/coverage',
      params: { endCustomerUnitId },
    },
    {
      title: t(($) => $.pages.unit.tabs.maintenance_history),
      to: '/units/$endCustomerUnitId/maintenance',
      params: { endCustomerUnitId },
    },
    {
      title: t(($) => $.pages.unit.tabs.diagnostics_log),
      to: '/units/$endCustomerUnitId/diagnostics',
      params: { endCustomerUnitId },
      hide: !enviroment.isQA,
    },
    {
      title: t(($) => $.pages.unit.tabs.services),
      to: '/units/$endCustomerUnitId/services',
      params: { endCustomerUnitId },
      hide: !showDigitalServices,
    },
    {
      title: t(($) => $.pages.unit.tabs.settings),
      to: '/units/$endCustomerUnitId/settings',
      params: { endCustomerUnitId },
    },
  ];

  const handleManualsClick = () => {
    if (serialNumbers.length <= 1) {
      window.open(`${manualsAndHandbooksBaseLink}${serialNumbers.join(',')}`, '_blank', 'noopener,noreferrer');
    } else {
      setShowManualsModal(true);
    }
  };

  return (
    <>
      <PageHeader
        isLoading={isLoadingUnit}
        title={unit?.metadata?.unitName || unit?.unitName || ''}
        subTitle={`${unit?.brand || ''}, ${unit?.unitModel || ''}`}
        isClaimingUnit={unit?.claiming}
        items={items}
        backButton={
          <IconButton icon="arrow-left" size="large" onClick={() => navigate({ to: '/units', search: { page } })} />
        }
        avatar={
          <UserMetadataAvatar isLoading={isLoadingUnit} size={56} url={unit?.metadata?.blobUrl} showPlaceholder />
        }
        actions={
          <>
            <Button
              variant="primary"
              size="medium"
              href={`${sparePartsBaseLink}${serialNumbers.join(',')}`}
              target="_blank"
              rel="noopener noreferrer"
              iconEnd="chevron-right"
              className="w-full md:w-fit"
            >
              {t(($) => $.pages.unit.spare_parts)}
            </Button>
            <Button variant="secondary" size="medium" onClick={handleManualsClick} className="w-full md:w-fit">
              {t(($) => $.pages.unit.tabs.manuals_handbooks)}
            </Button>
          </>
        }
        telemetryDeviceInfo={unit?.telemetryDeviceInfo}
        connectivityStatus={unit?.connectivityStatus?.status}
        serviceStatuses={unit?.serviceStatuses}
      />
      <ManualsSelectionModal
        open={showManualsModal}
        onClose={() => setShowManualsModal(false)}
        products={individualProducts || []}
      />
    </>
  );
};

export default withComponentErrorBoundary(UnitHeader);
